// src/SparepartsPages/Dpo/DpoDashboard.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Stack,
  Typography,
  TextField,
  Button,
  Divider,
  Chip,
  Drawer,
  Tabs,
  Tab,
  CircularProgress,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Sidebar from "../Sidebar/Sidebar";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";
import { Redirect } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/* helpers */
const fmt = (v) => (v ? String(v).slice(0, 10) : "");
const num = (v) => (v === null || v === undefined ? 0 : Number(v) || 0);

function useDebouncedValue(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const StatusChip = ({ v }) => {
  if (!v) return <Chip size="small" label="-" />;
  const color =
    v === "ALL_PRESENT"
      ? "success"
      : String(v).startsWith("MISSING_")
        ? "error"
        : "secondary";
  return <Chip size="small" color={color} label={v} />;
};

const PresentChip = ({ ok, label }) => (
  <Chip
    size="small"
    sx={{ mr: 0.5, mb: 0.5 }}
    color={ok ? "success" : "error"}
    variant={ok ? "outlined" : "filled"}
    label={ok ? label : `MISSING ${label}`}
  />
);

const DpoDashboard = () => {
  const userToken = LocalstorageHelper.GetUserToken();
  const isLoggedIn = !!userToken;

  // ✅ avoid multiple timeouts on every render
  useEffect(() => {
    const t = setTimeout(() => window.location.reload(false), 1800000);
    return () => clearTimeout(t);
  }, []);

  /* search + purchased filter */
  const [search, setSearch] = useState("");
  // ✅ debounced server search (invoice / dpo / party)
  const debouncedSearch = useDebouncedValue(search, 250);
  const searchPending = search !== debouncedSearch;

  const [purchased, setPurchased] = useState("ALL"); // ALL|YES|NO

  /* ✅ status filter (invoice list) */
  const [status, setStatus] = useState("ALL"); // ALL | ALL_PRESENT | MISSING_MAP | MISSING_TRANSIT | MISSING_CASE | OTHER

  /* grid */
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [gridLoading, setGridLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortModel, setSortModel] = useState([
    { field: "last_activity", sort: "desc" },
  ]);

  /* invoice drawer */
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState(0);
  const [activeInv, setActiveInv] = useState(null);

  const [dposLoading, setDposLoading] = useState(false);
  const [dpos, setDpos] = useState([]);

  const [trItemsLoading, setTrItemsLoading] = useState(false);
  const [trItems, setTrItems] = useState([]);

  const [csItemsLoading, setCsItemsLoading] = useState(false);
  const [csItems, setCsItems] = useState([]);

  /* ✅ DPO tab search (client-side, debounced) */
  const [dpoSearch, setDpoSearch] = useState("");
  const dpoSearchDeb = useDebouncedValue(dpoSearch, 250);
  const dpoSearchPending = dpoSearch !== dpoSearchDeb;

  /* dpo modal */
  const [dpoModalOpen, setDpoModalOpen] = useState(false);
  const [activeDpo, setActiveDpo] = useState(null);
  const [dpoItemsLoading, setDpoItemsLoading] = useState(false);
  const [dpoItems, setDpoItems] = useState([]);

  /* ✅ DPO items search (client-side, debounced) */
  const [dpoItemSearch, setDpoItemSearch] = useState("");
  const dpoItemSearchDeb = useDebouncedValue(dpoItemSearch, 250);
  const dpoItemSearchPending = dpoItemSearch !== dpoItemSearchDeb;

  const fetchJSON = useCallback(
    async (url, options = {}) => {
      const res = await fetch(url, {
        ...options,
        headers: { sptoken: userToken, ...(options.headers || {}) },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Request failed");
      return data;
    },
    [userToken]
  );

  const baseQS = useMemo(() => {
    const p = new URLSearchParams();
    if (debouncedSearch) p.set("search", debouncedSearch);
    p.set("purchased", purchased);

    // ✅ status filter
    if (status && status !== "ALL") p.set("status", status);

    return p.toString();
  }, [debouncedSearch, purchased, status]);

  // ✅ abort stale requests (fast paging / sorting)
  const gridAbortRef = useRef(null);

  const loadGrid = useCallback(async () => {
    if (gridAbortRef.current) gridAbortRef.current.abort();
    const controller = new AbortController();
    gridAbortRef.current = controller;

    setGridLoading(true);
    try {
      const sm = sortModel?.[0] || { field: "last_activity", sort: "desc" };
      const p = new URLSearchParams(baseQS);
      p.set("page", page);
      p.set("pageSize", pageSize);
      p.set("sortBy", sm.field || "last_activity");
      p.set("sortDir", sm.sort || "desc");

      const data = await fetchJSON(`/dpo/dashboard/invlist?${p.toString()}`, {
        signal: controller.signal,
      });

      setRows(Array.isArray(data.rows) ? data.rows : []);
      setRowCount(Number(data.rowCount || 0));
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(e.message);
    } finally {
      if (gridAbortRef.current === controller) setGridLoading(false);
    }
  }, [baseQS, fetchJSON, page, pageSize, sortModel]);

  const apply = useCallback(async () => {
    setPage(0);
    await loadGrid();
  }, [loadGrid]);

  useEffect(() => {
    if (!isLoggedIn) return;
    loadGrid();
  }, [loadGrid, isLoggedIn]);

  // ✅ abort stale invoice open requests (fast click)
  const invAbortRef = useRef(null);

  const openInvoice = useCallback(
    async (row) => {
      if (!row?.invno) return;

      if (invAbortRef.current) invAbortRef.current.abort();
      const controller = new AbortController();
      invAbortRef.current = controller;

      setActiveInv(row);
      setDrawerOpen(true);
      setDrawerTab(0);

      // reset drawer states
      setDpoSearch("");
      setDpos([]);
      setTrItems([]);
      setCsItems([]);

      setDposLoading(true);
      setTrItemsLoading(true);
      setCsItemsLoading(true);

      try {
        const inv = encodeURIComponent(row.invno);
        const [dpoRes, trRes, csRes] = await Promise.all([
          fetchJSON(`/dpo/dashboard/invoice/${inv}/dpos`, { signal: controller.signal }),
          fetchJSON(`/dpo/dashboard/invoice/${inv}/transit-items`, { signal: controller.signal }),
          fetchJSON(`/dpo/dashboard/invoice/${inv}/case-items`, { signal: controller.signal }),
        ]);

        setDpos(dpoRes.rows || []);
        setTrItems(trRes.rows || []);
        setCsItems(csRes.rows || []);
      } catch (e) {
        if (e?.name !== "AbortError") toast.error(e.message);
      } finally {
        if (invAbortRef.current === controller) {
          setDposLoading(false);
          setTrItemsLoading(false);
          setCsItemsLoading(false);
        }
      }
    },
    [fetchJSON]
  );

  // ✅ abort stale dpo items fetch
  const dpoAbortRef = useRef(null);

  const openDpoModal = useCallback(
    async (dpoRow) => {
      if (!dpoRow?.dpono) return;

      if (dpoAbortRef.current) dpoAbortRef.current.abort();
      const controller = new AbortController();
      dpoAbortRef.current = controller;

      setActiveDpo(dpoRow);
      setDpoModalOpen(true);

      setDpoItemSearch("");
      setDpoItems([]);
      setDpoItemsLoading(true);

      try {
        const r = await fetchJSON(
          `/dpo/dashboard/dpo/${encodeURIComponent(dpoRow.dpono)}/items`,
          { signal: controller.signal }
        );
        setDpoItems(Array.isArray(r.rows) ? r.rows.map((x, i) => ({ ...x, __rid: `${x?.partno || ""}-${i}` })) : []);
      } catch (e) {
        if (e?.name !== "AbortError") toast.error(e.message);
      } finally {
        if (dpoAbortRef.current === controller) setDpoItemsLoading(false);
      }
    },
    [fetchJSON]
  );

  // ✅ memoized columns (less re-render)
  const invCols = useMemo(
    () => [
      {
        field: "invno",
        headerName: "Invoice No",
        minWidth: 220,
        flex: 1,
        renderCell: (p) => (
          <Button size="small" onClick={() => openInvoice(p.row)}>
            {p.value}
          </Button>
        ),
      },
      {
        field: "invdate",
        headerName: "Inv Date",
        minWidth: 110,
        valueFormatter: (p) => fmt(p.value),
      },
      {
        field: "purchased",
        headerName: "Purchased",
        minWidth: 110,
        renderCell: (p) =>
          p.value ? (
            <Chip size="small" color="success" label="YES" />
          ) : (
            <Chip size="small" label="NO" />
          ),
      },
      {
        field: "status",
        headerName: "Status (Map/Transit/Case)",
        minWidth: 260,
        renderCell: (p) => <StatusChip v={p.value} />,
      },
      {
        field: "presence",
        headerName: "Presence",
        minWidth: 420,
        sortable: false,
        renderCell: (p) => (
          <Box sx={{ display: "flex", flexWrap: "wrap" }}>
            <PresentChip ok={!!p.row.has_map} label="DpoInvMap" />
            <PresentChip ok={!!p.row.has_transit} label="Transit" />
            <PresentChip ok={!!p.row.has_case} label="Case" />
          </Box>
        ),
      },
    ],
    [openInvoice]
  );

  const dpoCols = useMemo(
    () => [
      { field: "dpono", headerName: "DPO", minWidth: 260, flex: 1 },
      {
        field: "dpodate",
        headerName: "DPO Date",
        minWidth: 110,
        valueFormatter: (p) => fmt(p.value),
      },
      {
        field: "partyname",
        headerName: "Party",
        minWidth: 260,
        flex: 1,
        renderCell: (p) =>
          p.row.isstock ? (
            <Chip size="small" label="STOCK" />
          ) : (
            <Chip size="small" color="success" label={p.value || "STOCK"} />
          ),
      },
      {
        field: "status",
        headerName: "Mapped?",
        minWidth: 140,
        renderCell: (p) =>
          p.value === "DPO_UPLOADED" ? (
            <Chip size="small" color="success" label="MAPPED" />
          ) : (
            <Chip size="small" color="error" label="NOT MAPPED" />
          ),
      },
      {
        field: "action",
        headerName: "Open",
        minWidth: 110,
        sortable: false,
        renderCell: (p) => (
          <Button
            size="small"
            variant="outlined"
            onClick={() => openDpoModal(p.row)}
          >
            View
          </Button>
        ),
      },
    ],
    [openDpoModal]
  );

  const trCols = useMemo(
    () => [
      { field: "partno", headerName: "PartNo", minWidth: 160, flex: 1 },
      { field: "itemname", headerName: "Item", minWidth: 260, flex: 1.2 },
      { field: "qty", headerName: "Qty", width: 110, type: "number" },
      { field: "value", headerName: "Value", width: 130, type: "number" },
    ],
    []
  );

  const csCols = useMemo(
    () => [
      { field: "partno", headerName: "PartNo", minWidth: 160, flex: 1 },
      { field: "itemname", headerName: "Item", minWidth: 300, flex: 1.4 },
      { field: "qty", headerName: "Qty", width: 110, type: "number" },
      { field: "racknames", headerName: "Rack", minWidth: 220, flex: 1 },
    ],
    []
  );

  const dpoItemCols = useMemo(
    () => [
      { field: "partno", headerName: "PartNo", minWidth: 160, flex: 1 },
      { field: "itemname", headerName: "Item", minWidth: 260, flex: 1.3 },
      { field: "dpo_qty", headerName: "Inv Qty", width: 120, type: "number" },
    ],
    []
  );

  const filteredDpos = useMemo(() => {
    const q = String(dpoSearchDeb || "").trim().toLowerCase();
    if (!q) return dpos;

    return (Array.isArray(dpos) ? dpos : []).filter((r) => {
      const dpono = String(r?.dpono || "").toLowerCase();
      const party = String(r?.partyname || "").toLowerCase();
      const statusTxt = String(r?.status || "").toLowerCase();
      return dpono.includes(q) || party.includes(q) || statusTxt.includes(q);
    });
  }, [dpos, dpoSearchDeb]);

  const filteredDpoItems = useMemo(() => {
    const q = String(dpoItemSearchDeb || "").trim().toLowerCase();
    if (!q) return dpoItems;

    return (Array.isArray(dpoItems) ? dpoItems : []).filter((r) => {
      const part = String(r?.partno || "").toLowerCase();
      const item = String(r?.itemname || "").toLowerCase();
      return part.includes(q) || item.includes(q);
    });
  }, [dpoItems, dpoItemSearchDeb]);

  const dpoSearchAdornment = useMemo(
    () => (
      <InputAdornment position="end">
        <Stack direction="row" spacing={1} alignItems="center">
          {(dposLoading || dpoSearchPending) && <CircularProgress size={16} />}
          <Typography variant="caption" sx={{ minWidth: 44, textAlign: "right" }}>
            {filteredDpos.length}
          </Typography>
        </Stack>
      </InputAdornment>
    ),
    [dposLoading, dpoSearchPending, filteredDpos.length]
  );

  const dpoItemSearchAdornment = useMemo(
    () => (
      <InputAdornment position="end">
        <Stack direction="row" spacing={1} alignItems="center">
          {(dpoItemsLoading || dpoItemSearchPending) && <CircularProgress size={16} />}
          <Typography variant="caption" sx={{ minWidth: 44, textAlign: "right" }}>
            {filteredDpoItems.length}
          </Typography>
        </Stack>
      </InputAdornment>
    ),
    [dpoItemsLoading, dpoItemSearchPending, filteredDpoItems.length]
  );

  // cleanup: abort inflight requests when leaving page
  useEffect(() => {
    return () => {
      try {
        gridAbortRef.current?.abort?.();
        invAbortRef.current?.abort?.();
        dpoAbortRef.current?.abort?.();
      } catch {
        // ignore
      }
    };
  }, []);

  if (!isLoggedIn) return <Redirect exact to="/sparepartsLogin" />;

  return (
    <div>
      <ToastContainer position="top-right" autoClose={2000} newestOnTop />
      <Sidebar
        Title={"Invoice Dashboard"}
        expandedPanel={"panel2"}
        children={
          <Box sx={{ p: 1 }}>
            <Card sx={{ borderRadius: 2 }}>
              <CardHeader
                sx={{
                  py: 1,
                  "& .MuiCardHeader-title": { fontSize: 14, fontWeight: 700 },
                }}
                title="Transit Invoice Dashboard (linked with DpoInvMap + Case)"
                subheader={
                  <Typography variant="caption">
                    Main list is from Transit • Search optional • Filter Purchased
                    based on inwardid • Filter Status based on Map/Transit/Case presence
                  </Typography>
                }
              />
              <Divider />
              <CardContent sx={{ p: 1 }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Search Invoice / DPO / Party (optional)"
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(0);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") apply();
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {(searchPending || gridLoading) ? (
                              <CircularProgress size={18} />
                            ) : null}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <TextField
                      size="small"
                      select
                      fullWidth
                      label="Purchased"
                      value={purchased}
                      onChange={(e) => { setPurchased(e.target.value); setPage(0); }}
                    >
                      <MenuItem value="ALL">ALL</MenuItem>
                      <MenuItem value="YES">YES</MenuItem>
                      <MenuItem value="NO">NO</MenuItem>
                    </TextField>
                  </Grid>

                  {/* ✅ STATUS FILTER */}
                  <Grid item xs={12} md={2}>
                    <TextField
                      size="small"
                      select
                      fullWidth
                      label="Status"
                      value={status}
                      onChange={(e) => { setStatus(e.target.value); setPage(0); }}
                    >
                      <MenuItem value="ALL">ALL</MenuItem>
                      <MenuItem value="ALL_PRESENT">ALL_PRESENT</MenuItem>
                      <MenuItem value="MISSING_MAP">MISSING_MAP</MenuItem>
                      <MenuItem value="MISSING_TRANSIT">MISSING_TRANSIT</MenuItem>
                      <MenuItem value="MISSING_CASE">MISSING_CASE</MenuItem>
                      <MenuItem value="OTHER">OTHER</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={apply}
                      disabled={gridLoading}
                      fullWidth
                    >
                      Apply
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    {(gridLoading || searchPending) && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={18} />
                        <Typography variant="body2" color="text.secondary">
                          {gridLoading ? "Searching..." : "Searching..."}
                        </Typography>
                      </Stack>
                    )}
                  </Grid>
                </Grid>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ height: 620 }}>
                  <DataGrid
                    rows={rows}
                    columns={invCols}
                    getRowId={(r) => r.invno}
                    density="compact"
                    rowHeight={32}
                    loading={gridLoading}
                    rowCount={rowCount}
                    pagination
                    paginationMode="server"
                    page={page}
                    pageSize={pageSize}
                    onPageChange={(p) => setPage(p)}
                    onPageSizeChange={(s) => {
                      setPageSize(s);
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={(m) => setSortModel(m)}
                    disableRowSelectionOnClick
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                      toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 300 },
                      },
                    }}
                  />
                </Box>

                {/* Invoice Drawer */}
                <Drawer
                  anchor="right"
                  open={drawerOpen}
                  onClose={() => setDrawerOpen(false)}
                  PaperProps={{ sx: { width: { xs: "100%", md: 920 } } }}
                >
                  <Box sx={{ p: 2 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography variant="h6" sx={{ lineHeight: 1.1 }}>
                          Invoice: {activeInv?.invno || "-"}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          flexWrap="wrap"
                          sx={{ mt: 0.5 }}
                        >
                          <Chip size="small" label={`InvDate: ${fmt(activeInv?.invdate) || "-"}`} />
                          <Chip size="small" label={`Purchased: ${activeInv?.purchased ? "YES" : "NO"}`} />
                          <StatusChip v={activeInv?.status} />
                          <PresentChip ok={!!activeInv?.has_map} label="DpoInvMap" />
                          <PresentChip ok={!!activeInv?.has_transit} label="Transit" />
                          <PresentChip ok={!!activeInv?.has_case} label="Case" />
                        </Stack>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setDrawerOpen(false)}
                      >
                        Close
                      </Button>
                    </Stack>

                    <Divider sx={{ my: 1 }} />

                    <Tabs value={drawerTab} onChange={(e, v) => setDrawerTab(v)}>
                      <Tab label="DPOs" />
                      <Tab label="Transit Items" />
                      <Tab label="Case Items" />
                    </Tabs>

                    {drawerTab === 0 && (
                      <Box sx={{ mt: 1, height: 520 }}>
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Search DPO / Party / Status"
                            value={dpoSearch}
                            onChange={(e) => setDpoSearch(e.target.value)}
                            InputProps={{ endAdornment: dpoSearchAdornment }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setDpoSearch("")}
                            disabled={!dpoSearch}
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            Clear
                          </Button>
                        </Stack>

                        <DataGrid
                          rows={filteredDpos}
                          getRowId={(r) => r.dpono}
                          columns={dpoCols}
                          density="compact"
                          rowHeight={32}
                          disableRowSelectionOnClick
                          loading={dposLoading}
                          slots={{ toolbar: GridToolbar }}
                          slotProps={{
                            toolbar: {
                              showQuickFilter: false, // we already have our own search
                            },
                          }}
                        />
                      </Box>
                    )}

                    {drawerTab === 1 && (
                      <Box sx={{ mt: 1, height: 520 }}>
                        {trItemsLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DataGrid
                            rows={trItems}
                            getRowId={(r) => r.partno}
                            columns={trCols}
                            density="compact"
                            rowHeight={32}
                            disableRowSelectionOnClick
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{
                              toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 300 },
                              },
                            }}
                          />
                        )}
                      </Box>
                    )}

                    {drawerTab === 2 && (
                      <Box sx={{ mt: 1, height: 520 }}>
                        {csItemsLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DataGrid
                            rows={csItems}
                            getRowId={(r) => r.partno}
                            columns={csCols}
                            density="compact"
                            rowHeight={32}
                            disableRowSelectionOnClick
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{
                              toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 300 },
                              },
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                </Drawer>

                {/* DPO Modal */}
                <Dialog
                  open={dpoModalOpen}
                  onClose={() => setDpoModalOpen(false)}
                  maxWidth="lg"
                  fullWidth
                >
                  <DialogTitle sx={{ pr: 6 }}>
                    DPO: {activeDpo?.dpono || "-"}
                    <IconButton
                      onClick={() => setDpoModalOpen(false)}
                      sx={{ position: "absolute", right: 10, top: 10 }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>

                  <DialogContent dividers>
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                      <Chip size="small" label={`DPO Date: ${fmt(activeDpo?.dpodate) || "-"}`} />
                      <Chip
                        size="small"
                        label={`Party: ${activeDpo?.isstock ? "STOCK" : activeDpo?.partyname || "STOCK"}`}
                      />
                      <Chip
                        size="small"
                        color={activeDpo?.status === "DPO_UPLOADED" ? "success" : "error"}
                        label={activeDpo?.status === "DPO_UPLOADED" ? "MAPPED" : "NOT MAPPED"}
                      />
                      <Chip size="small" label={`Items: ${filteredDpoItems.length}`} />
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Search PartNo / Item"
                        value={dpoItemSearch}
                        onChange={(e) => setDpoItemSearch(e.target.value)}
                        InputProps={{ endAdornment: dpoItemSearchAdornment }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setDpoItemSearch("")}
                        disabled={!dpoItemSearch}
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        Clear
                      </Button>
                    </Stack>

                    <Box sx={{ height: 520 }}>
                      <DataGrid
                        rows={filteredDpoItems}
                        getRowId={(r) => r.__rid}
                        columns={dpoItemCols}
                        density="compact"
                        rowHeight={32}
                        disableRowSelectionOnClick
                        loading={dpoItemsLoading}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                          toolbar: {
                            showQuickFilter: false, // using our own search input
                          },
                        }}
                      />
                    </Box>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </Box>
        }
      />
    </div>
  );
};

export default DpoDashboard;
